/**
 * Point d'entrée unique pour appeler une fonction Edge.
 *
 * Les appels étaient auparavant écrits à la main dans chaque composant, avec
 * `Authorization: Bearer ${VITE_SUPABASE_ANON_KEY}`. C'est ce qui rendait tout
 * contrôle d'autorisation impossible côté serveur : la clé anonyme est
 * publique et n'identifie personne, donc la fonction ne pouvait pas savoir qui
 * l'appelait — et la plupart avaient fini par ne plus vérifier du tout.
 *
 * Ce module transmet le jeton de session de l'utilisateur connecté. Il échoue
 * franchement plutôt que de retomber sur la clé anonyme : un repli silencieux
 * redonnerait un appel anonyme qui « marche », et le contrôle serveur
 * réapparaîtrait comme une panne fonctionnelle plutôt que comme un refus.
 */

import { supabase } from './supabase';

export class EdgeFunctionError extends Error {
  constructor(
    message: string,
    public readonly status: number | null,
  ) {
    super(message);
    this.name = 'EdgeFunctionError';
  }
}

/**
 * En-tête `Authorization` à poser sur un appel de fonction Edge.
 *
 * Exporté pour les appels écrits à la main qui n'utilisent pas encore
 * `callEdgeFunction` — c'est un remplacement direct de
 * `` `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` ``, qui n'identifiait
 * personne. Préférer `callEdgeFunction` pour tout nouvel appel.
 */
export async function getAuthHeader(): Promise<string> {
  return authorizationHeader();
}

async function authorizationHeader(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw new EdgeFunctionError('Session illisible. Reconnectez-vous.', null);
  }
  const token = data.session?.access_token;
  if (!token) {
    throw new EdgeFunctionError('Session expirée. Reconnectez-vous.', 401);
  }
  return `Bearer ${token}`;
}

function baseUrl(): string {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) throw new EdgeFunctionError('VITE_SUPABASE_URL non défini', null);
  return url;
}

/**
 * Traduit une réponse en erreur lisible.
 *
 * Le serveur répond `{ error: "..." }` ; on préfère ce message au corps brut,
 * mais on ne se fie pas à ce qu'il soit toujours du JSON — une fonction qui
 * plante avant son gestionnaire d'erreur renvoie du texte.
 */
async function toError(response: Response): Promise<EdgeFunctionError> {
  const raw = await response.text().catch(() => '');
  let message = raw;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.error === 'string') message = parsed.error;
  } catch {
    // corps non JSON : on garde le texte tel quel
  }
  if (response.status === 401) {
    message = message || 'Session expirée. Reconnectez-vous.';
  } else if (response.status === 403) {
    message = message || "Vous n'avez pas les droits pour cette action.";
  }
  return new EdgeFunctionError(message || `Erreur HTTP ${response.status}`, response.status);
}

/** Appelle une fonction Edge et rend sa réponse JSON. */
export async function callEdgeFunction<T = unknown>(
  name: string,
  body: unknown = {},
): Promise<T> {
  const response = await fetch(`${baseUrl()}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authorizationHeader(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await toError(response);
  return (await response.json()) as T;
}

/**
 * Appelle une fonction Edge qui rend un fichier (PDF, CSV) et déclenche son
 * téléchargement. Le nom du fichier est fixé par l'appelant : l'en-tête
 * `Content-Disposition` ne survit pas au passage par un blob.
 *
 * Rend les en-têtes de la réponse, pour les fonctions qui y disent quelque
 * chose de plus que le fichier (`X-Listes-Manquantes` du PDF de la semaine).
 */
export async function downloadFromEdgeFunction(
  name: string,
  body: unknown,
  filename: string,
): Promise<Headers> {
  const response = await fetch(`${baseUrl()}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authorizationHeader(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await toError(response);

  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Libéré dans tous les cas : un objet URL non révoqué retient le blob en mémoire.
    URL.revokeObjectURL(href);
  }
  return response.headers;
}
